import React from 'react';
import { Box, Typography } from '@mui/material';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import NumbersIcon from '@mui/icons-material/Numbers';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import PersonIcon from '@mui/icons-material/Person';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import NotesIcon from '@mui/icons-material/Notes';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { neutral } from '_lex/_foundations/phxColors';
import { Field, FieldTypeEnum } from 'views/App/Portal/PerformanceReporting/FieldBank/_types';
import { getCategoryColor } from 'views/App/Portal/PerformanceReporting/_utils';

// Field type icons mapping - using actual MUI icons
const getFieldTypeIcon = (fieldType: FieldTypeEnum) => {
  switch (fieldType) {
    case 'Text':
      return <TextFieldsIcon fontSize='small' />;
    case 'Number':
      return <NumbersIcon fontSize='small' />;
    case 'Boolean':
      return <CheckBoxIcon fontSize='small' />;
    case 'Date':
      return <CalendarTodayIcon fontSize='small' />;
    case 'Time':
      return <AccessTimeIcon fontSize='small' />;
    case 'SingleSelect':
    case 'MultiSelect':
      return <MenuIcon fontSize='small' />;
    case 'Lookup':
      return <SearchIcon fontSize='small' />;
    case 'Attachment':
      return <AttachFileIcon fontSize='small' />;
    case 'BodyImage':
      return <PersonIcon fontSize='small' />;
    case 'VehicleImage':
      return <DirectionsCarIcon fontSize='small' />;
    case 'Narrative':
      return <NotesIcon fontSize='small' />;
    default:
      return <TextFieldsIcon fontSize='small' />;
  }
};

interface FieldDragOverlayProps {
  field: Field;
  dragHandlePosition?: 'left' | 'right';
}

const FieldDragOverlay: React.FC<FieldDragOverlayProps> = ({ field, dragHandlePosition = 'right' }) => {
  // Get category color for this field
  const categoryColor = getCategoryColor(field.fieldCategory);

  // Different styling for fields from sections vs field bank
  const isFromSection = dragHandlePosition === 'left';

  // Drag handle component to reuse
  const DragHandle = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        color: neutral[400],
        flexShrink: 0,
      }}
    >
      <DragIndicatorIcon fontSize='small' />
    </Box>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 2,
        border: '1px solid',
        borderColor: neutral[200],
        borderLeft: isFromSection ? '1px solid' : '4px solid',
        borderLeftColor: isFromSection ? neutral[200] : categoryColor,
        borderRadius: 1,
        backgroundColor: 'background.paper',
        boxShadow: 3,
        opacity: 0.95,
        minWidth: 350,
        maxWidth: 350,
        cursor: 'grabbing',
      }}
    >
      {/* Show drag handle on left for fields from sections */}
      {dragHandlePosition === 'left' && DragHandle}

      {/* Field type icon */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 24,
          height: 24,
          flexShrink: 0,
          color: categoryColor,
        }}
      >
        {getFieldTypeIcon(field.fieldType)}
      </Box>

      {/* Field info */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant='body2'
          fontWeight='600'
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: 'text.primary',
            fontSize: '0.875rem',
          }}
        >
          {field.name}
        </Typography>
        <Typography
          variant='caption'
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: 'text.secondary',
            fontSize: '0.75rem',
            mt: 0.25,
          }}
        >
          {field.fieldCategory?.name || 'Demographics'}
        </Typography>
      </Box>

      {/* Show drag handle on right for fields from field bank */}
      {dragHandlePosition === 'right' && DragHandle}
    </Box>
  );
};

export default FieldDragOverlay;
